import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, ClipboardList, Search } from 'lucide-react';
import type { AppState, KnowledgeItem, KnowledgeType, LearningPlan, LearningPlansByChild, LearningStatus } from '../types';
import { getCharacterStrokeCount } from '../data/character-strokes';
import { ChildAvatar, Segmented } from './ui';
import {
  getChildProgress,
  getDueCount,
  getLearningPlanIds,
  getPlanCount
} from '../lib/srs';

type PlanFilter = 'all' | 'planned' | 'available';
type CharacterSort = 'strokes' | 'pinyin';

const pinyinCollator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
const titleCollator = new Intl.Collator('zh-CN');

function normalizePinyin(item: KnowledgeItem) {
  return (item.pronunciation || item.title)
    .replace(/[眉菛菢菤菧]/g, 'v')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/伞/g, 'g')
    .toLowerCase();
}

function compareByPinyin(left: KnowledgeItem, right: KnowledgeItem) {
  return pinyinCollator.compare(normalizePinyin(left), normalizePinyin(right))
    || titleCollator.compare(left.title, right.title);
}

function compareCharacters(left: KnowledgeItem, right: KnowledgeItem, sort: CharacterSort) {
  if (sort === 'pinyin') return compareByPinyin(left, right);
  return getCharacterStrokeCount(left.title) - getCharacterStrokeCount(right.title)
    || compareByPinyin(left, right);
}

function uniqueIds(value: unknown): LearningPlan {
  return Array.isArray(value)
    ? [...new Set(value.filter((itemId): itemId is string => typeof itemId === 'string'))]
    : [];
}

function buildPlansByChild(state: AppState): LearningPlansByChild {
  if (state.learningPlansByChild) {
    return Object.fromEntries(state.children.map((child) => [
      child.id,
      uniqueIds(state.learningPlansByChild?.[child.id])
    ]));
  }
  const sharedPlan = uniqueIds(state.learningPlan);
  return Object.fromEntries(state.children.map((child) => [child.id, [...sharedPlan]]));
}

const statusMeta: Record<LearningStatus, { label: string; className: string }> = {
  unlearned: { label: '未学', className: 'is-gray' },
  learning: { label: '学习中', className: 'is-blue' },
  mastered: { label: '已学', className: 'is-green' }
};

const typeLabel: Record<KnowledgeType, string> = {
  character: '汉字',
  poem: '古诗',
  word: '英文单词'
};

export function LearningPlanManager({
  state,
  updateState,
  onToast,
  onBack
}: {
  state: AppState;
  updateState: (updater: (current: AppState) => AppState) => void;
  onToast: (message: string) => void;
  onBack: () => void;
}) {
  const [type, setType] = useState<KnowledgeType>('character');
  const [filter, setFilter] = useState<PlanFilter>('all');
  const [query, setQuery] = useState('');
  const [characterSort, setCharacterSort] = useState<CharacterSort>('strokes');
  const [visibleCount, setVisibleCount] = useState(48);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selecting, setSelecting] = useState(false);
  const [targetChildIds, setTargetChildIds] = useState<string[]>(() => {
    const active = state.children.find((child) => child.id === state.activeChildId);
    const fallback = active ?? state.children[0];
    return fallback ? [fallback.id] : [];
  });

  const children = state.children;

  useEffect(() => {
    setTargetChildIds((current) => {
      const existing = current.filter((childId) => children.some((child) => child.id === childId));
      if (existing.length) return existing;
      const fallback = children.find((child) => child.id === state.activeChildId) ?? children[0];
      return fallback ? [fallback.id] : [];
    });
  }, [children, state.activeChildId]);

  useEffect(() => {
    setVisibleCount(48);
    setSelectedIds(new Set());
    setSelecting(false);
  }, [type, filter, query, characterSort, targetChildIds]);

  const planIdsByChild = useMemo(() => {
    const map = new Map<string, Set<string>>();
    children.forEach((child) => map.set(child.id, getLearningPlanIds(state, child.id)));
    return map;
  }, [state, children]);

  const targetChildIdSet = useMemo(() => new Set(targetChildIds), [targetChildIds]);
  const targetChildren = useMemo(
    () => children.filter((child) => targetChildIdSet.has(child.id)),
    [children, targetChildIdSet]
  );
  const targetPlanIds = useMemo(() => {
    const union = new Set<string>();
    targetChildIds.forEach((childId) => {
      planIdsByChild.get(childId)?.forEach((itemId) => union.add(itemId));
    });
    return union;
  }, [planIdsByChild, targetChildIds]);

  const typeKnowledge = useMemo(
    () => state.knowledge.filter((item) => item.type === type),
    [state.knowledge, type]
  );

  const queryFiltered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return typeKnowledge;
    if (type === 'character') return typeKnowledge.filter((item) => item.title.trim() === needle);
    return typeKnowledge.filter((item) => (
      [item.title, item.meaning, item.pronunciation, item.author, item.dynasty, item.content]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    ));
  }, [type, typeKnowledge, query]);

  const plannedCountsByChild = useMemo(() => {
    const counts = new Map<string, number>();
    children.forEach((child) => {
      const ids = planIdsByChild.get(child.id);
      counts.set(child.id, ids
        ? typeKnowledge.reduce((count, item) => count + (ids.has(item.id) ? 1 : 0), 0)
        : 0);
    });
    return counts;
  }, [children, planIdsByChild, typeKnowledge]);

  const filtered = useMemo(() => queryFiltered
    .filter((item) => {
      if (filter === 'all') return true;
      return filter === 'planned' ? targetPlanIds.has(item.id) : !targetPlanIds.has(item.id);
    })
    .sort((left, right) => {
      if (type === 'character') return compareCharacters(left, right, characterSort);
      const leftLearned = targetChildIds.some((childId) => getChildProgress(state, childId, left.id));
      const rightLearned = targetChildIds.some((childId) => getChildProgress(state, childId, right.id));
      if (leftLearned !== rightLearned) return leftLearned ? -1 : 1;
      return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
    }), [queryFiltered, filter, targetPlanIds, state, targetChildIds, type, characterSort]);

  const visible = filtered.slice(0, visibleCount);
  const visibleIds = visible.map((item) => item.id);
  const allVisibleAssigned = visibleIds.length > 0 && visibleIds.every((itemId) => (
    targetChildIds.every((childId) => planIdsByChild.get(childId)?.has(itemId))
  ));
  const filterCounts = {
    all: queryFiltered.length,
    planned: queryFiltered.filter((item) => targetPlanIds.has(item.id)).length,
    available: queryFiltered.filter((item) => !targetPlanIds.has(item.id)).length
  };
  const primaryTargetChildId = targetChildIds[0];
  const typeCounts = {
    character: getPlanCount(state, 'character', primaryTargetChildId),
    poem: getPlanCount(state, 'poem', primaryTargetChildId),
    word: getPlanCount(state, 'word', primaryTargetChildId)
  };
  const dueCount = targetChildIds.reduce(
    (sum, childId) => sum + (childId ? getDueCount(state, childId, type) : 0),
    0
  );
  const masteredInPlan = targetChildIds.reduce((sum, childId) => (
    sum + typeKnowledge.filter((item) => (
      targetPlanIds.has(item.id) && getChildProgress(state, childId, item.id)?.masteredAt
    )).length
  ), 0);
  const targetNames = targetChildren.map((child) => child.name).join('、');

  const assignedChildIds = (itemId: string) => children
    .filter((child) => planIdsByChild.get(child.id)?.has(itemId))
    .map((child) => child.id);
  const assignedToTargets = (itemId: string) => targetChildIds.filter((childId) => planIdsByChild.get(childId)?.has(itemId));
  const allTargetsAssigned = (itemId: string) => (
    targetChildIds.length > 0 && targetChildIds.every((childId) => planIdsByChild.get(childId)?.has(itemId))
  );

  const toggleTargetChild = (childId: string) => {
    setTargetChildIds((current) => {
      if (current.includes(childId)) {
        if (current.length === 1) {
          onToast('请至少保留一个孩子');
          return current;
        }
        return current.filter((entry) => entry !== childId);
      }
      return [...current, childId];
    });
  };

  const applyChange = (current: AppState, itemIds: string[], mode: 'add' | 'remove') => {
    if (!itemIds.length || !targetChildIds.length) return current;
    const plansByChild = buildPlansByChild(current);
    targetChildIds.forEach((childId) => {
      const nextIds = new Set(plansByChild[childId] ?? []);
      itemIds.forEach((itemId) => {
        if (mode === 'add') nextIds.add(itemId);
        else nextIds.delete(itemId);
      });
      plansByChild[childId] = [...nextIds];
    });
    return {
      ...current,
      learningPlansByChild: plansByChild,
      learningPlan: [...new Set(Object.values(plansByChild).flat())],
      updatedAt: new Date().toISOString()
    };
  };

  const updatePlan = (itemIds: string[], mode: 'add' | 'remove') => {
    if (!itemIds.length || !targetChildIds.length) return;
    updateState((current) => applyChange(current, itemIds, mode));
    setSelectedIds(new Set());
    setSelecting(false);
    const action = mode === 'add' ? '加入' : '移出';
    onToast(`已为「${targetNames}」${action} ${itemIds.length} 项`);
  };

  const toggleSelected = (itemId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  return (
    <section className="subpage">
      <header className="subpage__header">
        <button className="icon-button" type="button" onClick={onBack} aria-label="返回"><ArrowLeft size={21} /></button>
        <span className="subpage__icon"><ClipboardList size={22} /></span>
        <div><h1>学习计划</h1><p>给一个或多个孩子分别安排学习内容</p></div>
        <div className="subpage__action">
          <button className="secondary-button" type="button" onClick={() => setSelecting((value) => !value)}>
            <Check size={18} />{selecting ? '完成选择' : '批量选择'}
          </button>
        </div>
      </header>

      <div className="subpage__content">
        <div className="plan-shared-note">
          <ClipboardList size={23} />
          <span>
            <strong>同一份知识可以同时安排给多个孩子</strong>
            <small>{children.length > 1 ? '先选择「安排给」的孩子，再加入或移出计划；每个孩子的学习进度仍独立记录。' : '加入计划后，该知识会出现在孩子的每日学习中，学习进度独立记录。'}</small>
          </span>
        </div>

        {children.length ? (
          <section className="plan-assignee-panel" aria-label="安排给哪些孩子">
            <header>
              <div>
                <strong>安排给</strong>
                <small>可同时勾选多个孩子，下面的操作会对所选孩子一起生效</small>
              </div>
              <span>已选 {targetChildIds.length} 人</span>
            </header>
            <div className="plan-child-select">
              {children.map((child) => {
                const active = targetChildIdSet.has(child.id);
                return (
                  <button
                    key={child.id}
                    className={active ? 'is-active' : ''}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleTargetChild(child.id)}
                  >
                    <ChildAvatar child={child} size="small" active={active} />
                    <span>
                      <strong>{child.name}</strong>
                      <small>已安排 {plannedCountsByChild.get(child.id) ?? 0} 项{typeLabel[type]}</small>
                    </span>
                    <i className="plan-child-select__check">{active ? <Check size={14} /> : null}</i>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        <section className="plan-summary-grid">
          <article>
            <small>所选{typeLabel[type]}计划</small>
            <strong>{typeCounts[type]}</strong>
            <span>{targetNames ? `${targetNames} 的计划` : '先选择孩子'}</span>
          </article>
          <article><small>待复习</small><strong>{dueCount}</strong><span>{targetNames ? `${targetNames} 合计` : '先选择孩子'}</span></article>
          <article><small>已掌握</small><strong>{masteredInPlan}</strong><span>{targetNames ? `${targetNames} 合计` : '暂无进度'}</span></article>
        </section>

        <Segmented
          ariaLabel="学习计划知识类型"
          value={type}
          onChange={setType}
          items={[
            { value: 'character', label: '汉字', icon: <span>汉</span>, count: typeCounts.character },
            { value: 'poem', label: '古诗', icon: <span>诗</span>, count: typeCounts.poem },
            { value: 'word', label: '英文单词', icon: <span>A</span>, count: typeCounts.word }
          ]}
        />

        <div className="plan-filter-row">
          <Segmented
            ariaLabel="学习计划筛选"
            value={filter}
            onChange={setFilter}
            items={[
              { value: 'all', label: '全部', count: filterCounts.all },
              { value: 'planned', label: '计划中', count: filterCounts.planned },
              { value: 'available', label: '未加入', count: filterCounts.available }
            ]}
          />
          {type === 'character' ? (
            <div className="plan-sort-field">
              <span>排序</span>
              <Segmented
                ariaLabel="汉字排序方式"
                value={characterSort}
                onChange={setCharacterSort}
                items={[
                  { value: 'strokes', label: '笔画从少到多' },
                  { value: 'pinyin', label: '拼音 A-Z' }
                ]}
              />
            </div>
          ) : null}
        </div>

        <div className="knowledge-search-row">
          <label className="knowledge-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={type === 'character' ? '按汉字本身精确搜索' : type === 'poem' ? '搜索诗名、作者或内容关键词' : '搜索英文单词或中文含义'}
            />
          </label>
          <span>显示 {Math.min(visible.length, filtered.length)} / {filtered.length}</span>
        </div>

        <div className="plan-bulk-shortcuts">
          <button className="text-button" type="button" disabled={!filtered.length || !targetChildIds.length} onClick={() => updatePlan(filtered.map((item) => item.id), 'add')}>
            加入所选孩子的 {filtered.length} 项
          </button>
          <button className="text-button" type="button" disabled={!filterCounts.planned || !targetChildIds.length} onClick={() => updatePlan(queryFiltered.filter((item) => targetPlanIds.has(item.id)).map((item) => item.id), 'remove')}>
            移出所选孩子的 {filterCounts.planned} 项
          </button>
        </div>

        {selecting ? (
          <div className="plan-batch-bar">
            <button className="secondary-button" type="button" onClick={() => setSelectedIds((current) => {
              const next = new Set(current);
              if (allVisibleAssigned) visibleIds.forEach((itemId) => next.delete(itemId));
              else visibleIds.forEach((itemId) => next.add(itemId));
              return next;
            })}>
              <Check size={17} />{allVisibleAssigned ? '取消本页' : '全选本页'}
            </button>
            <span>已选 {selectedIds.size} 项 · 安排给 {targetNames || '未选择'}</span>
            <button className="primary-button" type="button" disabled={!selectedIds.size} onClick={() => updatePlan([...selectedIds], 'add')}>加入所选</button>
            <button className="secondary-button" type="button" disabled={!selectedIds.size} onClick={() => updatePlan([...selectedIds], 'remove')}>移出所选</button>
          </div>
        ) : null}

        <div className={`plan-grid ${selecting ? 'is-selecting' : ''}`}>
          {visible.map((item) => {
            const assignedIds = assignedChildIds(item.id);
            const targetAssignedIds = assignedToTargets(item.id);
            const inPlan = targetPlanIds.has(item.id);
            const allAssigned = allTargetsAssigned(item.id);
            const isSelected = selectedIds.has(item.id);
            const cardStatus = targetChildIds.reduce<LearningStatus>((overall, childId) => {
              const progress = getChildProgress(state, childId, item.id);
              const childStatus: LearningStatus = !progress ? 'unlearned' : progress.masteredAt ? 'mastered' : 'learning';
              if (overall === 'mastered' && childStatus !== 'mastered') return 'learning';
              if (overall === 'unlearned' && childStatus !== 'unlearned') return 'learning';
              return overall === 'learning' ? 'learning' : childStatus;
            }, 'unlearned');
            return (
              <article className={`plan-card ${inPlan ? 'is-planned' : ''} ${isSelected ? 'is-selected' : ''}`} key={item.id}>
                {selecting ? (
                  <button className={`plan-select-toggle ${isSelected ? 'is-active' : ''}`} type="button" onClick={() => toggleSelected(item.id)} aria-label={`选择${item.title}`}>
                    <span>{isSelected ? <Check size={15} /> : null}</span>
                  </button>
                ) : null}
                <div className={`plan-card__visual plan-card__visual--${item.type}`}>
                  {item.type === 'character' ? <strong>{item.title}</strong> : null}
                  {item.type === 'poem' ? <><span>诗</span><strong>{item.title.slice(0, 1)}</strong></> : null}
                  {item.type === 'word' ? <><span>{item.emoji || 'A'}</span><strong>{item.title.slice(0, 1).toUpperCase()}</strong></> : null}
                </div>
                <div className="plan-card__body">
                  <header>
                    <div>
                      <strong>{item.title}</strong>
                      <small>{item.type === 'poem' ? `${item.dynasty || ''} · ${item.author || '佚名'}` : item.meaning || item.pronunciation || typeLabel[item.type]}</small>
                    </div>
                    {assignedIds.length ? <em>{assignedIds.length} 人已安排</em> : null}
                  </header>
                  <div className="plan-card__children">
                    {children.map((child) => {
                      const planned = assignedIds.includes(child.id);
                      const progress = getChildProgress(state, child.id, item.id);
                      const status: LearningStatus = !progress ? 'unlearned' : progress.masteredAt ? 'mastered' : 'learning';
                      return (
                        <span className={`${planned ? 'is-planned' : ''} ${targetChildIdSet.has(child.id) ? 'is-target' : ''}`} key={child.id}>
                          <b>{child.name}</b>
                          <em>{planned ? statusMeta[status].label : '未安排'}</em>
                        </span>
                      );
                    })}
                  </div>
                  <div className="plan-card__meta">
                    <span className={`status-pill ${statusMeta[cardStatus].className}`}>{statusMeta[cardStatus].label}</span>
                    <small>{targetNames ? `所选孩子已安排 ${targetAssignedIds.length}/${targetChildIds.length} 人` : '请先选择孩子'}</small>
                  </div>
                </div>
                {!selecting ? (
                  <button
                    className={allAssigned ? 'secondary-button plan-toggle-button' : 'primary-button plan-toggle-button'}
                    type="button"
                    disabled={!targetChildIds.length}
                    onClick={() => updatePlan([item.id], allAssigned ? 'remove' : 'add')}
                  >
                    {allAssigned ? '移出所选' : inPlan ? '补齐所选' : '加入所选'}
                  </button>
                ) : null}
              </article>
            );
          })}
        </div>

        {visible.length < filtered.length ? (
          <div className="knowledge-load-more">
            <button className="secondary-button" type="button" onClick={() => setVisibleCount((count) => count + 48)}>加载更多</button>
          </div>
        ) : null}

        {!filtered.length ? (
          <div className="panel list-empty">
            <strong>没有符合条件的内容</strong>
            <p>可以搜索其他内容，或切换知识类型继续挑选。</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
